import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { NavLink } from 'react-router-dom'
import { cn } from 'src/lib/utils'
import {
  Search,
  Bell,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Wallet,
  Plus
} from 'lucide-react'
import { Button } from 'src/components/ui/button'
import { Input } from 'src/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from 'src/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
} from 'src/components/ui/sheet'
import ThemeToggle from './ThemeToggle'
import { AuthClear, selectEffectiveAgentInfo } from 'src/store'
import { useNavigate } from 'react-router-dom'
import { Get as ProviderGet } from 'src/provider'
import { BRAND_COLORS, BRAND_PRESETS } from 'src/styles/brandColors'
import { DATA_MONO } from 'src/styles/typography'
import Breadcrumb from './Breadcrumb'

export function Header({ setCommandOpen }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const agentInfo = useSelector(selectEffectiveAgentInfo)

  const [balanceCredit, setBalanceCredit] = useState(null)
  const [balanceToken, setBalanceToken] = useState(null)
  const [billingAccountId, setBillingAccountId] = useState(null)
  const [isLoadingBalance, setIsLoadingBalance] = useState(true)
  const [notificationsOpen, setNotificationsOpen] = useState(false)

  useEffect(() => {
    // Fetch current balance from billing account
    const fetchBalance = async () => {
      try {
        // Use the self-service billing_account endpoint (works for all permission levels)
        const billingData = await ProviderGet('billing_account')

        if (billingData?.id) {
          setBillingAccountId(billingData.id)
        }
        if (billingData?.balance_credit !== undefined) {
          setBalanceCredit(billingData.balance_credit)
        }
        if (billingData?.balance_token !== undefined) {
          setBalanceToken(billingData.balance_token)
        }
      } catch (error) {
        console.error('Failed to fetch balance:', error)
      } finally {
        setIsLoadingBalance(false)
      }
    }

    fetchBalance()
  }, [])

  const handleLogout = () => {
    dispatch(AuthClear())
    navigate('/login')
  }

  const getInitials = (name) => {
    if (!name) return 'U'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <header className="h-16 flex-shrink-0">
      <div className="h-full px-6 flex items-center gap-4">
        {/* Breadcrumb navigation */}
        <Breadcrumb />

        {/* Spacer to push everything to the right */}
        <div className="flex-1" />

        {/* Right: Search bar, theme toggle, notifications, user menu */}
        <div className="flex items-center gap-2">
          {/* Search bar */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search or jump to..."
              className={cn("pl-9 pr-20 bg-stone-200/60 dark:bg-slate-900/80")}
              readOnly
              onClick={() => setCommandOpen?.(true)}
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
              <span className="text-xs">⌘</span>K
            </kbd>
          </div>

          {/* Current Balance */}
          {!isLoadingBalance && (balanceCredit !== null || balanceToken !== null) && billingAccountId && (
            <div className="hidden md:flex items-center gap-1">
              {/* Balance display - clickable */}
              <button
                onClick={() => navigate('/resources/billing_accounts/billing_accounts_detail')}
                className={cn("flex items-center gap-2 px-3 py-1.5 rounded-md border transition-colors cursor-pointer", BRAND_PRESETS.balance)}
                title="Tokens (T) are used for virtual-number calls, TTS, and recording. The USD figure is your prepaid credit balance. Click to view billing details."
              >
                <Wallet className={cn("h-4 w-4", BRAND_COLORS.text.accent, BRAND_COLORS.text.accentDark)} />
                {balanceToken !== null && (
                  <span className={cn("text-sm font-medium", DATA_MONO, BRAND_COLORS.text.primary, BRAND_COLORS.text.primaryDark)}>
                    {balanceToken.toLocaleString()} T
                  </span>
                )}
                {balanceToken !== null && balanceCredit !== null && (
                  <span className="text-muted-foreground text-xs">|</span>
                )}
                {balanceCredit !== null && (
                  <span className={cn("text-sm font-medium", DATA_MONO, BRAND_COLORS.text.primary, BRAND_COLORS.text.primaryDark)}>
                    ${(balanceCredit / 1000000).toFixed(2)}
                  </span>
                )}
              </button>

              {/* Top Up button */}
              <Button
                size="sm"
                variant="outline"
                className={cn("h-auto py-1.5 px-3 gap-1", BRAND_COLORS.accent.light, BRAND_COLORS.accent.dark, BRAND_COLORS.accent.lightHover, BRAND_COLORS.accent.darkHover, BRAND_COLORS.text.primary, BRAND_COLORS.text.primaryDark)}
                onClick={() => navigate('/resources/billing_accounts/billing_accounts_detail')}
                title="Top up balance"
              >
                <Plus className="h-3 w-3" />
                <span className="text-xs">Top Up</span>
              </Button>
            </div>
          )}

          {/* Theme toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setNotificationsOpen(true)}
          >
            <Bell className="h-5 w-5" />
          </Button>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={agentInfo?.avatar} />
                  <AvatarFallback>{getInitials(agentInfo?.name)}</AvatarFallback>
                </Avatar>
                <span className="hidden md:inline">{agentInfo?.name || 'User'}</span>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{agentInfo?.name}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {agentInfo?.username}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/resources/agents/agents_profile')}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Notifications Sidebar */}
      <Sheet open={notificationsOpen} onOpenChange={setNotificationsOpen}>
        <SheetContent side="right" className="w-[400px] sm:w-[540px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Notifications</h2>
              <p className="text-sm text-muted-foreground">
                Stay updated with your latest notifications
              </p>
            </div>

            {/* Placeholder for notifications */}
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-sm text-muted-foreground">
                No new notifications
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                We'll notify you when something important happens
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  )
}

export default Header
